import React from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  DocumentArrowUpIcon,
  PrinterIcon,
  BoltIcon,
  CubeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const ProductsHeader = ({ stats, onExport, onAdd, onQuickAdd }) => {
  return (
    <div className="space-y-6">
      {/* Title and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            ניהול מוצרים
          </h1>
          <p className="text-gray-600 mt-2">ניהול קטלוג המוצרים, מלאי ותמחור</p>
        </div>

        <div className="flex flex-wrap gap-3 mt-4 sm:mt-0">
          {onExport && (
            <button
              onClick={onExport}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
            >
              <PrinterIcon className="h-4 w-4" />
              הדפס דוח מלאי
            </button>
          )}

          <Link
            to="/admin/products/import"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
          >
            <DocumentArrowUpIcon className="h-4 w-4" />
            יבוא מוצרים
          </Link>

          {onQuickAdd && (
            <button
              onClick={onQuickAdd}
              className="flex items-center gap-2 px-4 py-2 border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-lg hover:from-blue-100 hover:to-indigo-100 hover:border-blue-400 transition-all shadow-sm hover:shadow-md"
              title="יצירה מהירה - שדות חיוניים + תמונה"
            >
              <BoltIcon className="h-5 w-5" />
              יצירה מהירה
            </button>
          )}

          <button
            onClick={onAdd}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
          >
            <PlusIcon className="h-4 w-4" />
            הוסף מוצר
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Products */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">סה"כ מוצרים</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <CubeIcon className="h-8 w-8" />
              </div>
            </div>
            <div className="absolute bottom-0 right-0 opacity-10">
              <ChartBarIcon className="h-24 w-24 transform translate-x-8 translate-y-8" />
            </div>
          </div>

          {/* Active Products */}
          <div className="relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">פעילים</p>
                <p className="text-3xl font-bold">{stats.active}</p>
                <p className="text-green-100 text-xs mt-1">
                  {stats.total > 0 ? `${Math.round((stats.active / stats.total) * 100)}%` : '0%'} מהמוצרים
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
            </div>
          </div>

          {/* Low Stock */}
          <div className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium mb-1">מלאי נמוך</p>
                <p className="text-3xl font-bold">{stats.lowStock}</p>
                {stats.lowStock > 0 && (
                  <p className="text-orange-100 text-xs mt-1">
                    דורש תשומת לב ⚠️
                  </p>
                )}
              </div>
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <ExclamationTriangleIcon className="h-8 w-8" />
              </div>
            </div>
          </div>

          {/* Inactive Products */}
          <div className="relative overflow-hidden bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-100 text-sm font-medium mb-1">לא פעילים</p>
                <p className="text-3xl font-bold">{stats.total - stats.active}</p>
                <p className="text-gray-100 text-xs mt-1">
                  {stats.total > 0 ? `${Math.round(((stats.total - stats.active) / stats.total) * 100)}%` : '0%'} מהמוצרים
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <CubeIcon className="h-8 w-8 opacity-60" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsHeader;

